import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function PropertyFormPage({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: 'residential',
    category: '',
    price: '',
    location: '',
    city: '',
    state: 'Tamil Nadu',
    area_sqft: '',
    bedrooms: '',
    bathrooms: '',
    amenities: [],
    images: [],
    virtual_tour_url: '',
    latitude: '',
    longitude: '',
    status: 'available',
    featured: false
  });
  const [newAmenity, setNewAmenity] = useState('');
  const [newImage, setNewImage] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [priceDisplay, setPriceDisplay] = useState('');
  const [priceUnit, setPriceUnit] = useState('lakh');
  const [mapLink, setMapLink] = useState('');
  const [resolvingMap, setResolvingMap] = useState(false);

  const fetchProperty = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`, {
        withCredentials: true
      });
      const property = response.data;

      // Set form data with proper conversions
      setFormData({
        ...property,
        price: property.price?.toString() || '',
        area_sqft: property.area_sqft?.toString() || '',
        bedrooms: property.bedrooms?.toString() || '',
        bathrooms: property.bathrooms?.toString() || '',
        latitude: property.latitude?.toString() || '',
        longitude: property.longitude?.toString() || ''
      });

      if (property.price) {
        if (property.price >= 10000000) {
          setPriceDisplay((property.price / 10000000).toString());
          setPriceUnit('crore');
        } else {
          setPriceDisplay((property.price / 100000).toString());
          setPriceUnit('lakh');
        }
      }
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Property not found');
      navigate('/admin-portal/properties');
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) {
      fetchProperty();
    }
  }, [id, isEdit, fetchProperty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const finalPrice = parseFloat(priceDisplay) * (priceUnit === 'crore' ? 10000000 : 100000);
      const data = {
        ...formData,
        price: finalPrice,
        area_sqft: parseFloat(formData.area_sqft),
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      if (isEdit) {
        await axios.put(`${API}/properties/${id}`, data, { withCredentials: true });
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API}/properties`, data, { withCredentials: true });
        toast.success('Property created successfully');
      }
      navigate('/admin-portal/properties');
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error('Failed to save property');
    } finally {
      setLoading(false);
    }
  };

  const addAmenity = () => {
    if (newAmenity.trim()) {
      setFormData({ ...formData, amenities: [...formData.amenities, newAmenity.trim()] });
      setNewAmenity('');
    }
  };

  const removeAmenity = (index) => {
    setFormData({
      ...formData,
      amenities: formData.amenities.filter((_, i) => i !== index)
    });
  };

  const addImage = () => {
    if (newImage.trim()) {
      setFormData({ ...formData, images: [...formData.images, newImage.trim()] });
      setNewImage('');
    }
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({
        ...formData,
        images: [...formData.images, response.data.url]
      });
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const resolveMapLink = async () => {
    if (!mapLink) {
      toast.error('Please paste a Google Maps link first');
      return;
    }
    setResolvingMap(true);
    try {
      const response = await axios.get(`${API}/resolve-map-link`, {
        params: { url: mapLink.trim() },
        withCredentials: true
      });
      setFormData({
        ...formData,
        latitude: response.data.latitude.toString(),
        longitude: response.data.longitude.toString()
      });
      toast.success('Location coordinates extracted successfully!');
    } catch (error) {
      console.error('Error resolving map:', error);
      toast.error('Failed to resolve map link. Please make sure it is a valid Google Maps link.');
    } finally {
      setResolvingMap(false);
    }
  };

  return (
    <AdminLayout user={user}>
      <div className="max-w-4xl mx-auto">
        <Button
          data-testid="back-button"
          onClick={() => navigate('/admin-portal/properties')}
          variant="ghost"
          className="mb-6 text-primary hover:bg-primary/10"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Properties
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white p-8 border border-stone-200 rounded-sm"
        >
          <h1 className="font-heading text-3xl font-medium text-primary mb-6">
            {isEdit ? 'Edit Property' : 'Add New Property'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary mb-2">Property Title *</label>
                <Input
                  data-testid="property-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="e.g., Luxury 3BHK Villa in Coimbatore"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Property Type *</label>
                <Select
                  value={formData.property_type}
                  onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                >
                  <SelectTrigger data-testid="property-type-select" className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="agricultural">Agricultural</SelectItem>
                    <SelectItem value="land">Land</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Category *</label>
                <Input
                  data-testid="category-input"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  placeholder="e.g., Villa, Flat, Plot"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Price *</label>
                <div className="flex gap-2">
                  <Input
                    data-testid="price-input"
                    type="number"
                    step="any"
                    value={priceDisplay}
                    onChange={(e) => setPriceDisplay(e.target.value)}
                    required
                    placeholder="e.g., 50"
                    className="flex-1 h-12"
                  />
                  <Select value={priceUnit} onValueChange={setPriceUnit}>
                    <SelectTrigger className="w-[120px] h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lakh">Lakhs</SelectItem>
                      <SelectItem value="crore">Crores</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Area (sqft) *</label>
                <Input
                  data-testid="area-input"
                  type="number"
                  value={formData.area_sqft}
                  onChange={(e) => setFormData({ ...formData, area_sqft: e.target.value })}
                  required
                  placeholder="e.g., 2000"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Location *</label>
                <Input
                  data-testid="location-input"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  placeholder="e.g., R.S. Puram"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">City *</label>
                <Input
                  data-testid="city-input"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  placeholder="e.g., Coimbatore"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Bedrooms</label>
                <Input
                  data-testid="bedrooms-input"
                  type="number"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                  placeholder="e.g., 3"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Bathrooms</label>
                <Input
                  data-testid="bathrooms-input"
                  type="number"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                  placeholder="e.g., 2"
                  className="h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Status *</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="status-select" className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="under_offer">Under Offer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary mb-2">Description *</label>
                <Textarea
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  placeholder="Describe the property..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary mb-2">Amenities</label>
                <div className="flex gap-2 mb-3">
                  <Input
                    data-testid="amenity-input"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    placeholder="Add amenity..."
                    className="h-10"
                  />
                  <Button
                    data-testid="add-amenity-button"
                    type="button"
                    onClick={addAmenity}
                    className="h-10 bg-primary text-white hover:bg-primary/90"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.amenities.map((amenity, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-secondary text-primary rounded-sm text-sm"
                    >
                      {amenity}
                      <button
                        data-testid={`remove-amenity-${index}`}
                        type="button"
                        onClick={() => removeAmenity(index)}
                        className="hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary mb-2">Property Images</label>

                {/* Image Upload */}
                <div className="mb-4 p-4 border-2 border-dashed border-stone-200 rounded-sm">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className={`flex flex-col items-center justify-center cursor-pointer py-4 ${uploadingImage ? 'opacity-50' : ''}`}
                  >
                    {uploadingImage ? (
                      <div className="text-center">
                        <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-2"></div>
                        <p className="text-sm text-[#57534E]">Uploading...</p>
                      </div>
                    ) : (
                      <>
                        <svg className="w-12 h-12 text-[#57534E] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-sm font-medium text-primary">Click to upload image</p>
                        <p className="text-xs text-[#57534E] mt-1">PNG, JPG, JPEG (max 5MB)</p>
                      </>
                    )}
                  </label>
                </div>

                {/* OR divider */}
                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-stone-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-[#57534E]">OR paste image URL</span>
                  </div>
                </div>

                {/* URL Input */}
                <div className="flex gap-2 mb-3">
                  <Input
                    data-testid="image-url-input"
                    value={newImage}
                    onChange={(e) => setNewImage(e.target.value)}
                    placeholder="Paste image URL..."
                    className="h-10"
                  />
                  <Button
                    data-testid="add-image-button"
                    type="button"
                    onClick={addImage}
                    className="h-10 bg-primary text-white hover:bg-primary/90"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Image Grid */}
                <div className="grid grid-cols-3 gap-4">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img src={image} alt={`Property ${index + 1}`} className="w-full h-24 object-cover rounded-sm" />
                      <button
                        data-testid={`remove-image-${index}`}
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary mb-2">Virtual Tour URL</label>
                <Input
                  data-testid="virtual-tour-input"
                  value={formData.virtual_tour_url}
                  onChange={(e) => setFormData({ ...formData, virtual_tour_url: e.target.value })}
className="h-12"
                />
              </div>

              <div className="md:col-span-2 bg-[#FFFBF5] p-6 border border-stone-200 rounded-sm">
                <h3 className="font-heading text-xl font-medium text-primary mb-4">Location & Map</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Google Maps Link</label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Paste Google Maps link (e.g., https://maps.app.goo.gl/...)"
                        value={mapLink}
                        onChange={(e) => setMapLink(e.target.value)}
                        className="h-12 bg-white"
                      />
                      <Button 
                        type="button" 
                        onClick={resolveMapLink} 
                        disabled={resolvingMap}
                        className="h-12 bg-primary text-white hover:bg-primary/90 px-6"
                      >
                        {resolvingMap ? 'Resolving...' : 'Extract Coordinates'}
                      </Button>
                    </div>
                    <p className="text-xs text-[#57534E] mt-2 italic">
                      Tip: Open the location in Google Maps, click "Share", and copy the link.
                    </p>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="featured-checkbox"
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-5 h-5 text-primary rounded"
                  />
                  <span className="text-sm font-medium text-primary">Mark as Featured Property</span>
                </label>
              </div>
            </div>

            <div className="flex gap-4 pt-6 border-t border-stone-200">
              <Button
                data-testid="submit-property-button"
                type="submit"
                disabled={loading}
                className="flex-1 h-12 bg-primary text-white hover:bg-primary/90 rounded-sm font-medium"
              >
                {loading ? 'Saving...' : isEdit ? 'Update Property' : 'Create Property'}
              </Button>
              <Button
                data-testid="cancel-button"
                type="button"
                onClick={() => navigate('/admin-portal/properties')}
                variant="outline"
                className="flex-1 h-12 border-2 border-stone-200 hover:bg-[#FFFBF5] rounded-sm font-medium"
              >
                Cancel
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AdminLayout>
  );
}